/**
 * A fan follows its order on the order's own Realtime Broadcast topic,
 * 'order:<order id>'. The database pushes { id, status, payment_status } there
 * on every status change (broadcast_order_status, migration
 * 20261001170000_close_public_order_reads.sql). The orders table itself is not
 * readable by fans: the unguessable order id the fan holds is the key.
 */
export const ORDER_UPDATE_EVENT = 'order_update';

export function orderStatusTopic(orderId: string): string {
  return `order:${orderId}`;
}

export interface OrderStatusUpdate {
  status: string;
  payment_status?: string;
}

/** The part of a Realtime channel this needs (structural, so tests can fake it). */
interface BroadcastChannelLike<C> {
  on(type: 'broadcast', filter: { event: string }, callback: (message: { payload: unknown }) => void): C;
}

/**
 * Opens the order's channel and calls onUpdate for each status broadcast.
 * Returns the channel; the caller subscribes (to know when it is live) and
 * removes it.
 */
export function listenToOrderStatus<C extends BroadcastChannelLike<C>>(
  client: { channel(name: string): C },
  orderId: string,
  onUpdate: (update: OrderStatusUpdate) => void,
): C {
  return client.channel(orderStatusTopic(orderId)).on('broadcast', { event: ORDER_UPDATE_EVENT }, (message) => {
    const payload = message?.payload as { id?: string; status?: unknown; payment_status?: unknown } | null | undefined;
    if (!payload || payload.id !== orderId || typeof payload.status !== 'string') return;
    onUpdate({
      status: payload.status,
      ...(typeof payload.payment_status === 'string' ? { payment_status: payload.payment_status } : {}),
    });
  });
}
